import { AnyAction } from "redux";

import { hasOwnProperty, definedMap, proveNever } from "@glideapps/ts-necessities";

import {
    ActionType,
    UpdateTabDescriptionPayload,
    SetAppPayload,
    UpdateGlideAppStatePayload,
    AppIDPayload,
    SetSourcePadPayload,
    UpdateSerializedAppPayload,
    ReorderTabsPayload,
    UpdateAppPayload,
    AddTabPayload,
    RemoveTabPayload,
    ModifyTableColumnsPayload,
    SetCurrentTabPayload,
    SetTemplateStorePayload,
    SetSchemaPayload,
    ApplyAppModificationPayload,
    AddSourceMetadataPayload,
    RemoveSourceMetadataForNativeTablePayload,
    SetBuilderActionPayload,
    WriteNavStatePayload,
    SetReadOnlyAppPayload,
    MutateScreenDescriptionPayload,
} from "../actions/apps-actions";
import { EnvironmentBoundValueProducer } from "common/components/primitives";
import {
    SerializedApp,
    getAppFeatures,
    ScreenFlag,
    getAppTabs,
    getSourceMetadata,
    isolateAppDescription,
    AppKind,
    getAppKind,
} from "common/components/SerializedApp";
import {
    ScreenDescriptionKind,
    TabDescription,
    ScreenDescription,
    ArrayScreenDescription,
    TypeSchema,
    isSingleRelationType,
    findTable,
} from "common/generator/description";
import { setIntersect } from "collection-utils";
import { assert, defined, replaceArrayItem, panic, truthify } from "common/Support";
import { AppOther, AppEnvironment, EvaluatedScreen, LOADING, Evaluable } from "common/components/types";
import { AppsAndState, AppAndState, getCurrentAppAndState, SourcePadState, TemplateStoreState } from "../store/types";
import { getAppsSaver } from "../lib/apps-saver";
import {
    MaterialGlideAppState,
    appStateWithNavigationModel,
} from "webapp/chrome/smart/material-glide-app/material-glide-app";
import { Data } from "common/components/data";
import { arrayScreenUpdatesForUpdatedClassScreen, isChatTab, isShoppingCartTab } from "common/generator/screens";
import {
    RewritingComponentConfiguratorContext,
    makeSimpleComponentConfiguratorContext,
    makeSimpleAppDescriptionContext,
} from "common/generator/components/index";
import { AppNavigationModel, Pivots, RuntimeScreenFlags } from "webapp/lib/navigation-model";
import { getScreenProperty } from "common/generator/components/property-utils";
import { logDebugger, logError } from "common/debug-print";
import { modifyColumnsInSchema } from "common/generator/computed-columns";
import { pushUndoState, pushRestoreStateFunction } from "../lib/undo";
import { checkAppDescriptionConsistency, fixAppDescription } from "common/generator/fix-app";
import { trackEvent } from "../analytics";
import { EminenceFlags } from "common/Database/eminence";
import { buildAndEvaluateScreen } from "../lib/build-and-evaluate";
import { handlerForArrayScreenFormat } from "common/generator/array-screens";
import { makeArrayScreenFavoritesPivots, needFavoritesPivotsForType } from "common/generator/make-screen";
import { getUserProfileTableInfo } from "common/components/user-profile";
import { QuotaKind } from "common/Database/quotas";
import { BuilderAction, BuilderActionsForApp } from "common/Database/builder-actions";
import { asBoolean, getValueFromValueProducer } from "common/components/component-helpers";
import { getAppEnvironmentForAppID } from "sharedUI/app-state-context";

function getLoadedApp(appID: string, apps: AppsAndState): AppAndState {
    const appWithID = defined(apps[appID]);
    if (appWithID === "loading") {
        return panic(`Trying to update app which is being loaded: ${appID}`);
    }
    return appWithID;
}

function setSourcePad(appID: string, apps: AppsAndState, sourcePad: SourcePadState): AppsAndState {
    const app = getLoadedApp(appID, apps);
    const newApps = { ...apps };
    newApps[appID] = { ...app, sourcePad };
    return newApps;
}

interface UpdateSerializedAppResult {
    readonly newState: AppsAndState;
    readonly serializedApp: SerializedApp;
}

function setSerializedApp(
    currentAppID: string,
    apps: AppsAndState,
    serializedApp: SerializedApp
): UpdateSerializedAppResult {
    const appWithID = getLoadedApp(currentAppID, apps);
    const newState = { ...apps };
    newState[currentAppID] = { ...appWithID, serializedApp };
    return { newState, serializedApp };
}

function updateSerializedApp(
    currentAppID: string,
    apps: AppsAndState,
    updates: Partial<SerializedApp>
): UpdateSerializedAppResult {
    const appWithID = getLoadedApp(currentAppID, apps);
    const serializedApp = { ...appWithID.serializedApp, ...updates };
    return setSerializedApp(currentAppID, apps, serializedApp);
}

interface UpdateScreenDescriptionResult extends UpdateSerializedAppResult {
    readonly desc: ScreenDescription | false | undefined;
}

function updateScreenDescription(
    appID: string,
    apps: AppsAndState,
    serializedApp: SerializedApp,
    builderActions: BuilderActionsForApp,
    eminenceFlags: EminenceFlags,
    schema: TypeSchema,
    name: string,
    mutator: (
        old: ScreenDescription | undefined,
        ccc: RewritingComponentConfiguratorContext
    ) => ScreenDescription | undefined
): UpdateScreenDescriptionResult {
    // FIXME: We should really get the sender's config context
    const ccc = makeSimpleComponentConfiguratorContext(
        appID,
        serializedApp,
        builderActions,
        schema,
        getAppFeatures(serializedApp),
        eminenceFlags
    );

    const original = serializedApp.screenDescriptions[name] as ScreenDescription | undefined;
    let updatedDesc = original;
    let didDelete = false;
    if (updatedDesc === undefined) {
        updatedDesc = mutator(updatedDesc, ccc);
        if (updatedDesc === undefined) {
            return { newState: apps, serializedApp, desc: undefined };
        }
    } else {
        updatedDesc = mutator(updatedDesc, ccc);
        if (updatedDesc === undefined) {
            updatedDesc = undefined;
            didDelete = true;
        }
    }
    let desc = updatedDesc;

    let screenDescriptions: Record<string, ScreenDescription>;
    if (desc !== undefined) {
        screenDescriptions = { ...serializedApp.screenDescriptions, [name]: desc };
    } else {
        screenDescriptions = { ...serializedApp.screenDescriptions };
        delete screenDescriptions[name];
    }

    serializedApp = { ...serializedApp, screenDescriptions };

    if (
        original !== undefined &&
        desc !== undefined &&
        desc.kind === ScreenDescriptionKind.Array &&
        isSingleRelationType(desc.type)
    ) {
        const table = findTable(schema, desc.type);
        const oldHandler =
            original.kind === ScreenDescriptionKind.Array ? handlerForArrayScreenFormat(original.format) : undefined;
        const newHandler = handlerForArrayScreenFormat(desc.format);
        if (table !== undefined && newHandler !== undefined) {
            const originalScreen = original as ArrayScreenDescription;
            const originalTable = isSingleRelationType(originalScreen.type)
                ? findTable(schema, originalScreen.type)
                : undefined;
            const before = definedMap(originalTable, t => oldHandler?.getFavoritePivots(originalScreen, t));
            const after = newHandler.getFavoritePivots(desc, table);
            if (before?.get(table) !== after?.get(table)) {
                const withPivot = needFavoritesPivotsForType(
                    desc.type,
                    makeSimpleAppDescriptionContext(appID, serializedApp, builderActions, schema)
                );
                desc = { ...desc, pivots: makeArrayScreenFavoritesPivots(withPivot) };
                screenDescriptions = { ...screenDescriptions, [name]: desc };
                serializedApp = { ...serializedApp, screenDescriptions };
            }
        }
    }

    const { newState } = setSerializedApp(appID, apps, serializedApp);

    return { newState, serializedApp, desc: didDelete ? false : desc };
}

function updateAppState(appID: string, apps: AppsAndState, updates: Partial<MaterialGlideAppState>): AppsAndState {
    const app = getLoadedApp(appID, apps);
    const newApps = { ...apps };
    newApps[appID] = { ...app, appState: { ...app.appState, ...updates } as MaterialGlideAppState };
    return newApps;
}

function setAppAndState(appID: string, apps: AppsAndState, appAndState: AppAndState): AppsAndState {
    const newApps = { ...apps };
    newApps[appID] = appAndState;
    return newApps;
}

function withModifyApp(appID: string, result: AppsAndState, trackAsModify: boolean = true): AppsAndState {
    const app = result[appID];
    let numRowsUsedInApp: number | undefined;
    if (app !== undefined && app !== "loading") {
        const issues = checkAppDescriptionConsistency(app.serializedApp, undefined, false);
        if (issues !== undefined) {
            logError("Changed app description is inconsistent", issues);
            return panic("Inconsistent app description");
        }

        numRowsUsedInApp = app.quotas?.[QuotaKind.RowsUsed].current ?? 0;
    }

    if (trackAsModify) {
        trackEvent("user modified app", { app_id: appID, app_rows: numRowsUsedInApp });
    }

    getAppsSaver().setAppModified(appID);
    return result;
}

function updateTabDescription(
    apps: AppsAndState,
    appID: string,
    app: SerializedApp,
    index: number,
    tabDescription: TabDescription,
    forceRebuildScreen: boolean
): AppsAndState {
    const currentAppAndState = getCurrentAppAndState(appID, apps);
    const appEnvironment = getAppEnvironmentForAppID(appID);
    if (currentAppAndState === undefined || appEnvironment === undefined) return apps;

    const {
        sourcePad,
        appState,
        builderActions,
        reloadIssues,
        publishState,
        appMetadata,
        analytics,
        userID,
        zapierAPIKey,
        zaps,
        webhooks,
        quotas,
        grandfathering,
        serializedApp,
        serial,
    } = currentAppAndState;
    if (appState === undefined) return apps;

    const title = tabDescription.title ?? "";
    const oldTabs = getAppTabs(app);
    assert(index >= 0 && index < oldTabs.length);
    const currentTab = oldTabs[index];
    assert(tabDescription.propertyName === currentTab.propertyName);
    const newTabs = replaceArrayItem(oldTabs, index, tabDescription);
    let { navigationModel, functions } = appState;
    const usesOldNavigationModel = getAppKind(serializedApp) !== AppKind.Page;
    const rootPivots = usesOldNavigationModel ? navigationModel.getRootForTab(index) : undefined;
    const screenName = getScreenProperty(tabDescription.screenName);
    if (screenName === undefined) return apps;
    if (!forceRebuildScreen && screenName === getScreenProperty(currentTab.screenName) && rootPivots !== undefined) {
        for (const screen of rootPivots.pivots) {
            if (screen.title === undefined) continue;
            const { current } = screen.title;
            if (current !== LOADING) {
                pushRestoreStateFunction(
                    appID,
                    () => screen.title?.set(current),
                    () => screen.title?.set(title)
                );
            }
            screen.title.set(title);
        }
    } else if (rootPivots !== undefined) {
        const firstPivots = defined(rootPivots.pivots[0]);
        // FIXME: We're assuming here that all pivots have the same input context
        const { inputCtx, screenId, itemID } = defined(firstPivots);
        const { pivots, userDefinedComponents, isVisible } = buildAndEvaluateScreen(
            screenName,
            app,
            builderActions,
            sourcePad.schema,
            inputCtx,
            new Data<AppOther, AppEnvironment>(title),
            appEnvironment,
            functions,
            firstPivots.flags,
            screenId,
            itemID,
            tabDescription,
            forceRebuildScreen
        );
        navigationModel = navigationModel.replaceScreenStack(index, {
            pivots,
            isVisible,
        });
        functions = userDefinedComponents;
    }
    const result = setAppAndState(appID, apps, {
        sourcePad,
        serializedApp: {
            ...app,
            tabs: newTabs,
        },
        builderActions,
        appState: appStateWithNavigationModel(
            {
                ...appState,
                functions,
            },
            navigationModel
        ),
        reloadIssues,
        publishState,
        appMetadata,
        analytics,
        userID,
        zapierAPIKey,
        zaps,
        webhooks,
        quotas,
        grandfathering,
        serial,
    });
    return withModifyApp(appID, result);
}

const screenFlagsToPreserve = [ScreenFlag.IsTopScreen];

function makeAdditionalFlagsForScreen(existingScreenFlags: ReadonlySet<ScreenFlag>): ReadonlySet<ScreenFlag> {
    return setIntersect(screenFlagsToPreserve, existingScreenFlags);
}

function evaluateAndReplaceScreen(
    apps: AppsAndState,
    appID: string,
    screenName: string,
    updateResult: UpdateScreenDescriptionResult,
    forceReplaceTopAndParentScreens: boolean
): AppsAndState {
    const currentAppAndState = getCurrentAppAndState(appID, apps);
    if (currentAppAndState === undefined) return apps;

    const { sourcePad, appState, builderActions } = currentAppAndState;
    const appEnvironment = getAppEnvironmentForAppID(appID);
    if (appState === undefined || appEnvironment === undefined) return apps;
    const { schema } = sourcePad;

    let { navigationModel, functions } = appState;

    const modalParent = navigationModel.modalParentScreen;
    if (modalParent !== undefined && modalParent.screen.name === screenName) {
        navigationModel = navigationModel.popModalScreen();
    }

    const topScreen = defined(navigationModel.currentScreen);

    apps = updateResult.newState;
    const { desc } = updateResult;
    // FIXME: Do something about `false`, which means the screen is deleted
    if (desc === false || desc === undefined) return apps;

    let newSerializedApp = updateResult.serializedApp;

    if (forceReplaceTopAndParentScreens || screenName === topScreen.name) {
        const replacementScreens: Pivots[] = [];
        function evalScreen(name: string, { inputCtx, title, screenId, itemID, flags }: EvaluatedScreen) {
            const { pivots, userDefinedComponents } = buildAndEvaluateScreen(
                name,
                newSerializedApp,
                builderActions,
                schema,
                inputCtx,
                definedMap(title, t => new EnvironmentBoundValueProducer(t)),
                defined(appEnvironment),
                functions,
                makeAdditionalFlagsForScreen(flags),
                screenId,
                itemID,
                undefined,
                false
            );
            replacementScreens.push({ pivots });
            functions = userDefinedComponents;
        }

        const parentScreen = navigationModel.parentScreen;
        const parentScreenName = definedMap(parentScreen, p => p.screen.name);
        let replaceParentScreen = forceReplaceTopAndParentScreens;
        if (desc.kind === ScreenDescriptionKind.Class) {
            const updates = arrayScreenUpdatesForUpdatedClassScreen(
                appID,
                desc.type,
                newSerializedApp,
                builderActions,
                schema
            );
            for (const [updateScreenName, updateScreenUpdates] of updates) {
                // We rebuild the parent screen if it's an array screen, because its search
                // properties are dependent on this screen's description, and it might or
                // might not need pivots depending on the contents of this screen.
                if (parentScreenName === updateScreenName) {
                    replaceParentScreen = true;
                }

                if (updateScreenUpdates === undefined) continue;

                const arrayScreenUpdateResult = updateScreenDescription(
                    appID,
                    apps,
                    newSerializedApp,
                    builderActions,
                    appEnvironment.eminenceFlags,
                    schema,
                    updateScreenName,
                    old => {
                        if (old === undefined) return undefined;
                        return { ...old, updateScreenUpdates };
                    }
                );

                apps = arrayScreenUpdateResult.newState;
                newSerializedApp = arrayScreenUpdateResult.serializedApp;
            }
        }

        if (replaceParentScreen && parentScreenName !== undefined && parentScreen !== undefined) {
            evalScreen(parentScreenName, parentScreen.screen);
        }

        evalScreen(screenName, topScreen);

        // FIXME: Overwrite all screens with that name!  But we can't just replace them with
        // `screen`, because they will have different contexts.
        navigationModel = navigationModel.replaceTopScreens(replacementScreens);
    }

    apps = updateAppState(appID, apps, { navigationModel, functions });

    // This is for screen updates that require the context to update, too:
    for (const [i, tab] of getAppTabs(newSerializedApp).entries()) {
        if (getScreenProperty(tab.screenName) === screenName) {
            apps = updateTabDescription(apps, appID, newSerializedApp, i, tab, true);
        }
    }

    return apps;
}

function withRebuildCurrentScreen(apps: AppsAndState, appID: string): AppsAndState {
    const currentAppAndState = getCurrentAppAndState(appID, apps);
    if (currentAppAndState === undefined) return apps;

    const navigationModel = currentAppAndState.appState?.navigationModel;
    if (navigationModel === undefined) return apps;

    const screenName = navigationModel.currentScreen?.name;
    if (screenName === undefined) return apps;

    const { serializedApp } = currentAppAndState;
    const screen = serializedApp.screenDescriptions[screenName];
    if (screen === undefined) return apps;

    return evaluateAndReplaceScreen(apps, appID, screenName, { newState: apps, serializedApp, desc: screen }, false);
}

function rebuildAllScreensInNavigationModel(
    navigationModel: AppNavigationModel,
    serializedApp: SerializedApp,
    builderActions: ReadonlyMap<string, BuilderAction>,
    schema: TypeSchema,
    appEnvironment: AppEnvironment,
    functions: ReadonlyMap<string, Evaluable>
): { navigationModel: AppNavigationModel; functions: ReadonlyMap<string, Evaluable> } {
    navigationModel = navigationModel.replaceAllScreens(pivots => {
        if (pivots.pivots.length === 0) return pivots;
        const s = pivots.pivots[0];
        if (s.name === undefined) return pivots;
        const { pivots: newPivots, userDefinedComponents } = buildAndEvaluateScreen(
            s.name,
            serializedApp,
            builderActions,
            schema,
            s.ctx,
            definedMap(s.title, t => new EnvironmentBoundValueProducer(t)),
            appEnvironment,
            functions,
            makeAdditionalFlagsForScreen(s.flags),
            s.screenId,
            s.itemID,
            undefined,
            false
        );
        functions = userDefinedComponents;
        return { pivots: newPivots };
    });

    return { navigationModel, functions };
}

export default function glideAppState(maybeApps: AppsAndState | undefined, action: AnyAction): AppsAndState {
    if (maybeApps === undefined) {
        return panic("Root state elements must be defined");
    }
    const apps = maybeApps;

    const untypedPayload = action.payload as unknown;

    // FIXME: It would be nice if we didn't need this hack to distinguish whether an action
    // applies to us.
    if (!hasOwnProperty(untypedPayload, "appID")) {
        switch (action.type) {
            case ActionType.UnloadApps: {
                const removeAppIDs = untypedPayload as string[];
                const newApps: AppsAndState = {};
                for (const k of Object.keys(apps)) {
                    if (removeAppIDs.indexOf(k) >= 0) continue;
                    newApps[k] = apps[k];
                }
                return newApps;
            }

            default:
                return apps;
        }
    }

    const payload = untypedPayload as AppIDPayload;
    const { appID } = payload;

    const currentAppAndState = getCurrentAppAndState(appID, apps);
    // First we handle all the actions that don't need `currentAppAndState`

    switch (action.type) {
        case ActionType.SetSourcePad:
            const { data: sample } = payload as SetSourcePadPayload;
            return setSourcePad(appID, apps, sample);

        case ActionType.SetAppLoading: {
            const maybeApp = apps[appID];
            if (maybeApp !== undefined) {
                return apps;
            }
            const newApps = { ...apps };
            newApps[appID] = "loading";
            return newApps;
        }

        case ActionType.SetApp: {
            const { data: appAndState } = payload as SetAppPayload;
            const newApps = { ...apps };
            newApps[appID] = appAndState;
            return newApps;
        }

        case ActionType.SetSaveApp: {
            return withModifyApp(appID, apps, false);
        }
    }

    const appEnvironment = getAppEnvironmentForAppID(appID);
    if (currentAppAndState === undefined || appEnvironment === undefined) return apps;

    const { serializedApp, appState, sourcePad, builderActions } = currentAppAndState;
    const usesOldNavigationModel = getAppKind(serializedApp) !== AppKind.Page;

    // Then we handle all the actions that don't need `appState`

    switch (action.type) {
        case ActionType.UpdateApp: {
            const { updates, didModify } = (payload as UpdateAppPayload).data;
            let newApps = { ...apps };
            newApps[appID] = { ...currentAppAndState, ...updates } as AppAndState;
            // To switch ##showTimeInCalendar we unfortunately have to rebuild
            // the current screen whenever the column's format changes.  This
            // is where we're told that the schema changes.  We could go
            // through contortions to figure out whether we have to rebuild
            // the screen, or we could just do it, like so:
            if (updates.sourcePad !== undefined) {
                newApps = withRebuildCurrentScreen(newApps, appID);
            }
            if (updates.serializedApp !== undefined && didModify) {
                return withModifyApp(appID, newApps);
            } else {
                return newApps;
            }
        }

        case ActionType.UpdateSerializedApp: {
            const { data: updates } = payload as UpdateSerializedAppPayload;
            // FIXME: This has to update the running app title, too
            return withModifyApp(appID, updateSerializedApp(appID, apps, updates).newState);
        }

        case ActionType.MutateScreenDescription: {
            const { mutator, screenName } = payload as MutateScreenDescriptionPayload;

            return withModifyApp(
                appID,
                evaluateAndReplaceScreen(
                    apps,
                    appID,
                    screenName,
                    updateScreenDescription(
                        appID,
                        apps,
                        serializedApp,
                        builderActions,
                        appEnvironment.eminenceFlags,
                        sourcePad.schema,
                        screenName,
                        mutator
                    ),
                    false
                )
            );
        }

        case ActionType.UpdateTabDescription: {
            const { index, desc, forceRebuild } = payload as UpdateTabDescriptionPayload;
            return updateTabDescription(apps, appID, serializedApp, index, desc, forceRebuild);
        }

        case ActionType.AddSourceMetadata: {
            const { sourceMetadata } = payload as AddSourceMetadataPayload;
            return withModifyApp(
                appID,
                updateSerializedApp(appID, apps, {
                    sourceMetadataArray: [...getSourceMetadata(serializedApp), sourceMetadata],
                }).newState
            );
        }

        case ActionType.RemoveNativeTableSourceMetadata: {
            const { nativeTableID } = payload as RemoveSourceMetadataForNativeTablePayload;
            return withModifyApp(
                appID,
                updateSerializedApp(appID, apps, {
                    sourceMetadataArray: getSourceMetadata(serializedApp).filter(
                        sm => sm.type !== "Native table" || sm.id !== nativeTableID
                    ),
                }).newState
            );
        }
    }

    if (appState === undefined) return apps;

    // Lastly we handle all the actions that also need `appState`.

    switch (action.type) {
        case ActionType.UpdateGlideAppState: {
            const { data: f } = payload as UpdateGlideAppStatePayload;
            return updateAppState(appID, apps, f(appState));
        }

        case ActionType.ReorderTabs: {
            const { data: permutation } = payload as ReorderTabsPayload;
            const oldTabs = getAppTabs(serializedApp);
            assert(permutation.length === oldTabs.length);
            for (let i = 0; i < oldTabs.length; i++) {
                assert(permutation.indexOf(i) >= 0);
            }
            const tabs = permutation.map(to => oldTabs[to]);
            const newSerializedApp = { ...serializedApp, tabs };
            let newNavModel = appState.navigationModel.reorderTabs(permutation);

            const rebuilt = rebuildAllScreensInNavigationModel(
                newNavModel,
                newSerializedApp,
                builderActions,
                sourcePad.schema,
                appEnvironment,
                appState.functions
            );
            newNavModel = rebuilt.navigationModel;
            const functions = rebuilt.functions;

            const result = setAppAndState(appID, apps, {
                ...currentAppAndState,
                serializedApp: newSerializedApp,
                appState: { ...appStateWithNavigationModel(appState, newNavModel), functions },
            });
            return withModifyApp(appID, result);
        }

        case ActionType.AddTab: {
            const { tabDescription, index } = payload as AddTabPayload;

            const screenName = getScreenProperty(tabDescription.screenName);
            if (screenName === undefined) {
                logError("New tab has no screen name");
                return apps;
            }

            const screen = serializedApp.screenDescriptions[screenName];
            if (screen === undefined) {
                logError("Screen for new tab not found");
                return apps;
            }

            if (screen.kind === ScreenDescriptionKind.Class || screen.kind === ScreenDescriptionKind.Array) {
                if (screen.fetchesData !== true) {
                    logError("Screen must fetch its own data for new tab");
                    return apps;
                }
            } else {
                if (screen.kind !== ScreenDescriptionKind.Chat && screen.kind !== ScreenDescriptionKind.ShoppingCart) {
                    return proveNever(screen, `Unknown screen kind ${(screen as any).kind}`, apps);
                }
            }

            const oldTabs = getAppTabs(serializedApp);
            const newTabs = [...oldTabs.slice(0, index), tabDescription, ...oldTabs.slice(index)];
            const newSerializedApp = { ...serializedApp, tabs: newTabs };

            const flags: ScreenFlag[] = [ScreenFlag.IsTopScreen];
            const title = tabDescription.title ?? "";
            const { pivots, isVisible, userDefinedComponents } = buildAndEvaluateScreen(
                screenName,
                newSerializedApp,
                builderActions,
                defined(sourcePad).schema,
                // FIXME: This is a hack - we shouldn't have to know what the context for the screen
                // needs to look like for the title to show.
                new Data<AppOther, AppEnvironment>({ title: new Data<AppOther, AppEnvironment>(title) }),
                new Data<AppOther, AppEnvironment>(title),
                appEnvironment,
                appState.functions,
                new Set(flags),
                undefined,
                undefined,
                tabDescription,
                false
            );

            const newNavModel =
                getAppKind(serializedApp) === AppKind.App
                    ? appState.navigationModel.addTab({ pivots, isVisible }, index)
                    : appState.navigationModel;

            const result = setAppAndState(appID, apps, {
                ...currentAppAndState,
                serializedApp: { ...serializedApp, tabs: newTabs },
                appState: appStateWithNavigationModel(
                    {
                        ...appState,
                        functions: userDefinedComponents,
                    },
                    newNavModel
                ),
            });
            return withModifyApp(appID, result);
        }

        case ActionType.RemoveTab: {
            const { data: index } = payload as RemoveTabPayload;
            const oldTabs = getAppTabs(serializedApp);

            const toRemove = oldTabs[index];
            if (isShoppingCartTab(toRemove) || isChatTab(toRemove)) {
                logDebugger("Removing special tabs is not allowed.");
                return apps;
            }

            const newTabs = [...oldTabs.slice(0, index), ...oldTabs.slice(index + 1)];
            let newNavModel = appState.navigationModel;
            if (usesOldNavigationModel) {
                newNavModel = newNavModel.removeTab(index);
            }

            // We have to treat Flyout tabs as "hidden" for the purposes
            // of fixing the navigation model. If we don't, then deleting
            // a Tab Bar tab could replace it with a Flyout tab that removes
            // the Tab Bar and makes those tabs permanently inaccessible
            // (until page refresh).
            let visibleIndexes = newTabs.map((t, i) => (t.hidden || truthify(t.inFlyout) ? -1 : i)).filter(i => i >= 0);

            // It is technically possible to remove the singular non-flyout tab.
            // When this happens, we need to bump the first flyout tab out.
            if (visibleIndexes.length === 0 && newTabs.length > 0) {
                let firstTabIndex = newTabs.findIndex(t => !t.hidden);
                if (firstTabIndex < 0) {
                    // If we don't even have a tab in the flyout, all tabs are
                    // hidden, so we unhide the first tab.
                    firstTabIndex = 0;
                }
                newTabs[firstTabIndex] = { ...defined(newTabs[firstTabIndex]), inFlyout: false, hidden: false };
                visibleIndexes = [firstTabIndex];
            }

            if (usesOldNavigationModel && visibleIndexes.find(i => newNavModel.currentTabIndex === i) === undefined) {
                newNavModel = newNavModel.setTab(visibleIndexes[0]);
            }

            const result = setAppAndState(appID, apps, {
                ...currentAppAndState,
                serializedApp: { ...serializedApp, tabs: newTabs },
                appState: appStateWithNavigationModel(appState, newNavModel),
            });
            return withModifyApp(appID, result);
        }

        case ActionType.WriteNavState: {
            const { data: newModel } = payload as WriteNavStatePayload;
            const result = setAppAndState(appID, apps, {
                ...currentAppAndState,
                appState: appStateWithNavigationModel(appState, newModel),
            });
            return withModifyApp(appID, result);
        }

        case ActionType.EnsureTabVisible: {
            const { tabs } = serializedApp;
            if (tabs === undefined) {
                return apps;
            }
            const navModel = appState.navigationModel;
            const tabsWithVisible = navModel.isVisibles.map((vp, i) => {
                let visible = vp !== undefined ? asBoolean(getValueFromValueProducer(vp)) : true;
                if (tabs[i].hidden) {
                    visible = false;
                }
                return {
                    ...tabs[i],
                    isVisible: visible,
                };
            });
            const currentTab = tabsWithVisible[navModel.currentTabIndex];

            if (!currentTab.isVisible) {
                let index = tabsWithVisible.findIndex(t => t.isVisible && t.inFlyout !== true);
                if (index < 0) {
                    index = tabsWithVisible.findIndex(t => t.isVisible);
                }
                if (index >= 0) {
                    const result = setAppAndState(appID, apps, {
                        ...currentAppAndState,
                        appState: appStateWithNavigationModel(appState, navModel.setTab(index)),
                    });
                    return withModifyApp(appID, result);
                }
            }

            return apps;
        }

        case ActionType.SetCurrentTab: {
            if (!usesOldNavigationModel) return apps;

            const { data: index } = payload as SetCurrentTabPayload;

            const curNavModel = appState.navigationModel;
            let newNavModel = curNavModel;
            if (serializedApp.tabs?.[index]?.inFlyout === true) {
                newNavModel = curNavModel.pushScreen(curNavModel.getRootForTab(index), index, [
                    RuntimeScreenFlags.HideTabBar,
                ]);
            } else {
                newNavModel = curNavModel.setTab(index);
                while (newNavModel.isInModal()) {
                    newNavModel = newNavModel.popModalScreen();
                }
                newNavModel = newNavModel.popToRoot();
            }

            return setAppAndState(appID, apps, {
                ...currentAppAndState,
                appState: appStateWithNavigationModel(appState, newNavModel),
            });
        }

        case ActionType.ModifyTableColumns: {
            const { removed, modified, moved, setProtected, rowIDColumns } = payload as ModifyTableColumnsPayload;
            const { schema } = modifyColumnsInSchema(
                sourcePad.schema,
                getSourceMetadata(serializedApp),
                getUserProfileTableInfo(serializedApp),
                removed,
                modified,
                moved,
                setProtected,
                rowIDColumns,
                true
            );
            return { ...apps, [appID]: { ...currentAppAndState, sourcePad: { ...sourcePad, schema } } };
        }

        case ActionType.SetSchema: {
            const { schema } = payload as SetSchemaPayload;
            return { ...apps, [appID]: { ...currentAppAndState, sourcePad: { ...sourcePad, schema } } };
        }

        case ActionType.ApplyAppModification: {
            const {
                appModification: { serializedApp: sa, appState: as, mutateStateFunctions: fns },
                isRedo,
            } = payload as ApplyAppModificationPayload;
            defined(as.navigationModel.currentScreen).needsRerender = true;
            if (fns !== undefined) {
                for (const { undo, redo } of isRedo ? fns : Array.from(fns).reverse()) {
                    if (isRedo) {
                        redo();
                    } else {
                        undo();
                    }
                }
            }
            return withModifyApp(
                appID,
                setAppAndState(appID, apps, { ...currentAppAndState, serializedApp: sa, appState: as })
            );
        }

        case ActionType.PushUndoState: {
            pushUndoState({ appID, serializedApp, appState });
            return apps;
        }

        case ActionType.SetBuilderAction: {
            const {
                actionID,
                tableName,
                title,
                description,
                action: compoundAction,
            } = payload as SetBuilderActionPayload;
            const newActions = new Map(builderActions);
            newActions.set(actionID, {
                appIDs: [appID],
                action: compoundAction,
                title,
                description,
                perApp: { [appID]: { tableName } },
            });

            let newApps = setAppAndState(appID, apps, { ...currentAppAndState, builderActions: newActions });

            const topScreen = defined(appState.navigationModel.currentScreen);
            const screenName = topScreen.name;
            const screenDesc = definedMap(screenName, n => serializedApp.screenDescriptions[n]);
            if (
                screenName !== undefined &&
                (screenDesc?.kind === ScreenDescriptionKind.Class || screenDesc?.kind === ScreenDescriptionKind.Array)
            ) {
                const updateResult = { desc: screenDesc, newState: newApps, serializedApp };
                newApps = evaluateAndReplaceScreen(newApps, appID, screenName, updateResult, false);
            }

            return withModifyApp(appID, newApps);
        }

        case ActionType.SetReadOnlyApp: {
            const { data: appDescription } = payload as SetReadOnlyAppPayload;
            const newAppDesc = isolateAppDescription(
                fixAppDescription(appID, appDescription, builderActions, sourcePad.schema, appEnvironment.eminenceFlags)
            );
            const serializedAppUpdates: Partial<SerializedApp> = { ...newAppDesc, features: serializedApp.features };
            const newSerializedApp = { ...serializedApp, ...serializedAppUpdates };

            let { navigationModel, functions } = appState;

            const rebuilt = rebuildAllScreensInNavigationModel(
                navigationModel,
                newSerializedApp,
                builderActions,
                sourcePad.schema,
                appEnvironment,
                functions
            );
            navigationModel = rebuilt.navigationModel;
            functions = rebuilt.functions;

            return updateAppState(appID, updateSerializedApp(appID, apps, serializedAppUpdates).newState, {
                navigationModel,
                functions,
            });
        }

        default:
            return apps;
    }
}

export function templateStoreState(
    templateInfo: TemplateStoreState | undefined,
    action: AnyAction
): TemplateStoreState {
    switch (action.type) {
        case ActionType.SetTemplateInfo:
            const { appID, storeInfo } = action.payload as SetTemplateStorePayload;
            const basis = templateInfo ?? {};
            if (storeInfo === undefined) {
                delete basis[appID];
                return basis;
            } else {
                return { ...basis, [appID]: storeInfo };
            }
        default:
            return templateInfo ?? {};
    }
}
